import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Download, ChevronDown } from "lucide-react";

export default function HeroBannerGrado({
  image,
  content,
  showApply = false,
  applyUrl,
  showApplyPresencial = false,
  applyUrlPresencial,
  brochure,
  logo,
}) {
  // Estado para controlar el menú desplegable de modalidades
  const [isOpen, setIsOpen] = useState(false);

  // Verificamos si ambas opciones están activas y tienen URLs válidas
  const hasBothDropdown = showApply && applyUrl && showApplyPresencial && applyUrlPresencial;

  return (
    <section className="relative min-h-[55vh] ">
      {/* Imagen de fondo */}
      <img
        src={image}
        alt="Portada"
        className="absolute inset-0 h-full w-full object-cover"
      />

      {/* Overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(120deg,rgba(0,66,112,0.92)_0%,rgba(0,66,112,0.70)_45%,rgba(2,6,23,0.30)_100%)]" />

      {/* Contenido */}
      <div className="flex items-center relative z-10 w-full">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:py-20 w-full">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mx-auto max-w-4xl text-center"
          >
            {/* Contenedor del Logo alineado a la izquierda */}
            {logo && (
              <div className="flex justify-start mb-6">
                <img
                  src={logo}
                  alt="utpl"
                  className="max-h-[80px] w-auto object-contain"
                />
              </div>
            )}

            <div
              className="
                [&_h1]:mx-auto
                [&_h1]:text-white
                [&_h1]:text-4xl
                [&_h1]:lg:text-5xl
                [&_h1]:font-bold
                [&_h1]:leading-tight

                [&_h2]:mx-auto
                [&_h2]:text-white
                [&_h2]:text-3xl
                [&_h2]:lg:text-4xl
                [&_h2]:font-bold
                [&_h2]:leading-tight

                [&_h3]:text-white
                [&_h3]:text-2xl
                [&_h3]:m-0
                [&_h3]:p-0

                [&_p]:mx-auto
                [&_p]:mt-5
                [&_p]:text-lg
                [&_p]:leading-8
                [&_p]:text-slate-200
              "
              dangerouslySetInnerHTML={{
                __html: content ?? "",
              }}
            />

            {(showApply || showApplyPresencial || brochure) && (
              <div className="mt-10 flex flex-wrap justify-center gap-4 items-center">
                
                {/* CASO 1: Ambas modalidades están activas -> Botón con Desplegable */}
                {hasBothDropdown ? (
                  <div className="relative inline-block text-left">
                    <button
                      onClick={() => setIsOpen(!isOpen)}
                      className="inline-flex min-w-[220px] items-center justify-center gap-2 rounded-xl bg-amber-400 px-7 py-3.5 font-semibold text-[#004270] shadow-lg transition-all duration-300 hover:bg-amber-300 hover:shadow-xl"
                    >
                      Matricúlate ahora
                      <ChevronDown size={18} className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {/* Menú de opciones animado */}
                    <AnimatePresence>
                      {isOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.2 }}
                          className="absolute left-0 right-0 mt-2 z-20 mx-auto w-56 rounded-xl bg-white p-2 shadow-xl ring-1 ring-black/5"
                        >
                          <a
                            href={applyUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex w-full items-center justify-between rounded-lg px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100 transition-colors"
                          >
                            Modalidad en línea
                            <ArrowRight size={14} className="text-slate-400" />
                          </a>
                          <a
                            href={applyUrlPresencial}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex w-full items-center justify-between rounded-lg px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100 transition-colors"
                          >
                            Modalidad presencial
                            <ArrowRight size={14} className="text-slate-400" />
                          </a>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  /* CASO 2: Solo una modalidad está activa -> Botones Directos */
                  <>
                    {showApply && applyUrl && (
                      <a
                        href={applyUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex min-w-[220px] items-center justify-center gap-2 rounded-xl bg-amber-400 px-7 py-3.5 font-semibold text-[#004270] shadow-lg transition-all duration-300 hover:bg-amber-300 hover:text-[#004270] hover:shadow-xl"
                      >
                        Matricúlate ahora
                        <ArrowRight size={18} />
                      </a>
                    )}

                    {showApplyPresencial && applyUrlPresencial && (
                      <a
                        href={applyUrlPresencial}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex min-w-[220px] items-center justify-center gap-2 rounded-xl bg-amber-400 px-7 py-3.5 font-semibold text-[#004270] shadow-lg transition-all duration-300 hover:bg-amber-300 hover:text-[#004270] hover:shadow-xl"
                      >
                        Inscríbete ahora
                        <ArrowRight size={18} />
                      </a>
                    )}
                  </>
                )}

                {/* Botón de Brochure (Siempre visible si existe) */}
                {brochure && (
                  <a
                    href={brochure}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex min-w-[220px] items-center justify-center gap-2 rounded-xl border border-white/30 bg-white/10 px-7 py-3.5 font-semibold text-white backdrop-blur-sm transition-all duration-300 hover:bg-white/20"
                  >
                    <Download size={18} />
                    Descargar brochure
                  </a>
                )}
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}